import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Card, Alert, Button } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import { useTranslation } from "react-i18next";
import type { Partner } from "@kappa-v2/contracts";
import { PageHeader } from "../components/PageHeader.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { EmptyState } from "../components/EmptyState.js";
import { useApi } from "../hooks/useApi.js";
import { usePartners } from "../features/partners/hooks/usePartners.js";
import { usePartnersFilterStore } from "../store/partnersFilterStore.js";

import { formLabel } from "../lib/labels.js";
import { PartnerFilters, PARTNER_TYPE_OPTIONS } from "../features/partners/components/PartnerFilters.js";
import { PartnersGrid } from "../features/partners/components/PartnersGrid.js";
import { PartnerFormDialog } from "../features/partners/components/PartnerFormDialog.js";
import { EnrollmentWizardDialog } from "../features/partners/components/EnrollmentWizardDialog.js";

export function PartnersPage() {
  const { t } = useTranslation();
  const { rows, loading, saving, error, fetchAll, save, remove, toggleActive } = usePartners();
  const { openPdf, printPdf } = useApi();

  // Filtri inizializzati dallo store → persistono al ritorno dal dettaglio socio.
  const persistFilters = usePartnersFilterStore((s) => s.set);
  const savedFilters = useState(() => usePartnersFilterStore.getState())[0];
  const [search, setSearch] = useState(savedFilters.search);
  const [partnerType, setPartnerType] = useState(savedFilters.partnerType);
  const [active, setActive] = useState(savedFilters.active);

  useEffect(() => {
    persistFilters({ search, partnerType, active });
  }, [search, partnerType, active, persistFilters]);

  const pdfPath = useCallback(() => {
    const params = new URLSearchParams();
    if (active) params.set("active", active);
    if (partnerType) params.set("partnerType", partnerType);
    const qs = params.toString() ? `?${params}` : "";
    return `/api/v1/partners/pdf${qs}`;
  }, [active, partnerType]);

  // Mappa i18n dei tipi socio inviata al PDF: derivata dalle chiavi form.* (segue la lingua).
  const partnerTypeLabels = useMemo(
    () => Object.fromEntries(PARTNER_TYPE_OPTIONS.map((value) => [value, formLabel(value)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const exportPdf = useCallback(
    () => openPdf(pdfPath(), { body: partnerTypeLabels }),
    [openPdf, pdfPath, partnerTypeLabels]
  );
  const printList = useCallback(
    () => printPdf(pdfPath(), { body: partnerTypeLabels }),
    [printPdf, pdfPath, partnerTypeLabels]
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Partner | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Partner | null>(null);
  // Iscrizione guidata: si apre dopo la creazione di un socio o dal menu riga.
  const [enrollTarget, setEnrollTarget] = useState<Partner | null>(null);

  // Ricarica l'elenco rispettando il filtro attivi/inattivi corrente.
  const reload = useCallback(() => {
    fetchAll({ active: active || undefined });
  }, [fetchAll, active]);

  // Ricarica quando cambia il filtro attivi/inattivi (chiamata API)
  useEffect(() => {
    reload();
  }, [reload]);

  // Filtri client-side (ricerca testo + tipo) con ordinamento per rilevanza:
  // prima i cognomi che INIZIANO col testo digitato, poi il nome che inizia,
  // poi le corrispondenze parziali; a parità, ordine alfabetico per cognome.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      const matchSearch =
        !q ||
        `${r.firstName} ${r.lastName} ${r.fiscalCode ?? ""}`.toLowerCase().includes(q);
      const matchType = !partnerType || r.partnerType === partnerType;
      return matchSearch && matchType;
    });

    const byLastName = (a: Partner, b: Partner) =>
      `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, "it");

    if (!q) return [...list].sort(byLastName);

    const rank = (r: Partner) => {
      const last = (r.lastName ?? "").toLowerCase();
      const first = (r.firstName ?? "").toLowerCase();
      if (last.startsWith(q)) return 0;
      if (first.startsWith(q)) return 1;
      if (last.includes(q)) return 2;
      return 3;
    };

    return [...list].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      return ra !== rb ? ra - rb : byLastName(a, b);
    });
  }, [rows, search, partnerType]);

  const openAdd = () => {
    setEditTarget(null);
    setDialogOpen(true);
  };

  const openEdit = (p: Partner) => {
    setEditTarget(p);
    setDialogOpen(true);
  };

  const handleSave = useCallback(
    async (body: Partial<Partner>, id?: string) => {
      const saved = await save(body, id);
      fetchAll({ active: active || undefined });
      if (!id && saved?._id) setEnrollTarget(saved);
    },
    [save, fetchAll, active]
  );

  const handleToggleActive = useCallback(
    async (p: Partner) => {
      await toggleActive(p);
      fetchAll({ active: active || undefined });
    },
    [toggleActive, fetchAll, active]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget?._id) return;
    await remove(deleteTarget._id);
    setDeleteTarget(null);
    fetchAll({ active: active || undefined });
  }, [remove, deleteTarget, fetchAll, active]);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={t("partners.title")}
        subtitle={t("partners.subtitle", { count: rows.length })}
        actionLabel={t("partners.newPartner")}
        onAction={openAdd}
        onRefresh={reload}
        actions={
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={printList}
              disabled={loading || rows.length === 0}
            >
              {t("common.print")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportPdf}
              disabled={loading || rows.length === 0}
            >
              {t("common.exportPdf")}
            </Button>
          </>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Card>
          <PartnerFilters
            search={search}
            partnerType={partnerType}
            active={active}
            onSearchChange={setSearch}
            onTypeChange={setPartnerType}
            onActiveChange={setActive}
          />

          {!loading && filtered.length === 0 ? (
            <EmptyState
              title={t("partners.noneFound")}
              description={t("partners.noneFoundDesc")}
              actionLabel={t("partners.addPartner")}
              onAction={openAdd}
            />
          ) : (
            <PartnersGrid
              rows={filtered}
              loading={loading}
              onEdit={openEdit}
              onDelete={setDeleteTarget}
              onToggleActive={handleToggleActive}
              onEnroll={setEnrollTarget}
            />
          )}
        </Card>
      </Box>

      <PartnerFormDialog
        open={dialogOpen}
        partner={editTarget}
        saving={saving}
        onSave={handleSave}
        onClose={() => setDialogOpen(false)}
      />

      <EnrollmentWizardDialog
        open={Boolean(enrollTarget)}
        partner={enrollTarget}
        onClose={() => setEnrollTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("partners.deleteConfirm", { name: `${deleteTarget?.lastName ?? ""} ${deleteTarget?.firstName ?? ""}`.trim() })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
